import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";

import { Island } from "./Island";
import Stack from "./Stack";
import { ToolButton } from "./ToolButton";
import DropdownMenu from "./dropdownMenu/DropdownMenu";
import { ColorPicker } from "./ColorPicker/ColorPicker";
import { RadioSelection } from "./RadioSelection";
import { t } from "../i18n";

import {
  ArrowIcon,
  DoubleArrowIcon,
  LineIcon,
  RectangleIcon,
  EllipseIcon,
  DiamondIcon,
  TriangleIcon,
  PentagonIcon,
  FreedrawIcon,
  HighlighterIcon,
  CalloutIcon,
  OutlineArrowIcon,
  OutlineDoubleArrowIcon,
  HollowArrowIcon,
  HollowDoubleArrowIcon,
  FillHachureIcon,
  FillCrossHatchIcon,
  FillSolidIcon,
  ExportIcon,
  StrokeWidthBaseIcon,
  StrokeWidthBoldIcon,
  StrokeWidthExtraBoldIcon,
  TextAlignLeftIcon,
  TextAlignCenterIcon,
  TextAlignRightIcon,
} from "./icons";

import type { AppClassProperties, UIAppState, AppProps } from "../types";
import type { ExcalidrawElement } from "@excalidraw/element/types";
import {
  getFormValue,
  actionChangeTextAlign,
  actionChangeStrokeColor,
  actionChangeStrokeWidth,
  actionChangeFontSize,
  actionChangeFillStyle,
  actionChangeBackgroundColor,
} from "../actions/actionProperties";
import type { ActionManager } from "../actions/manager";
import { mutateElement } from "@excalidraw/element";
import { isTextElement } from "@excalidraw/element";
import {
  getBoundTextElement,
  getContainerElement,
  redrawTextBoundingBox,
  newElementWith,
} from "@excalidraw/element";
import {
  DEFAULT_ELEMENT_STROKE_COLOR_PALETTE,
  STROKE_WIDTH,
} from "@excalidraw/common";

type Props = {
  appState: UIAppState;
  app: AppClassProperties;
  actionManager: ActionManager;
  elements: readonly ExcalidrawElement[];
  showToolBar?: boolean;
};

export const LeftToolFlyouts: React.FC<Props> = ({
  appState,
  app,
  actionManager,
  elements,
  showToolBar = true,
}) => {
  if (!showToolBar) {
    return null;
  }
  const activeTool = appState.activeTool.type;
  // derive current values for shapes panel (reflect selection or tool defaults)
  const selectedStrokeWidth = getFormValue(
    elements,
    app as any,
    (element) => (element as any).strokeWidth,
    (element) => (element as any).hasOwnProperty("strokeWidth"),
    (hasSelection) =>
      !hasSelection ? (appState as any).currentItemStrokeWidth : null,
  ) as any;
  // 线条风格固定为朴素（solid + roughness 0），不再提供修改选项
  // remember if flyouts were dismissed during drawing; only reopen on tool change
  const dismissedRef = useRef(false);
  const [openGroup, setOpenGroup] = useState<
    null | "freedraw" | "line" | "shapes" | "text"
  >(null);
  const prevSelectedKeyRef = useRef<string>("");

  useEffect(() => {
    // any update to activeTool (even same type) re-allows flyouts
    dismissedRef.current = false;
  }, [appState.activeTool]);

  // 只读模式下关闭所有飞出菜单
  useEffect(() => {
    if (appState.viewModeEnabled && openGroup) {
      setOpenGroup(null);
    }
  }, [appState.viewModeEnabled, openGroup]);

  // toggle behavior from main toolbar: same button toggles, other buttons close
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { group: typeof openGroup };
      const group = detail?.group ?? null;
      dismissedRef.current = false;
      setOpenGroup((prev) => (prev === group ? null : group));
    };
    window.addEventListener("leftflyout:toggle", handler as any);
    return () =>
      window.removeEventListener("leftflyout:toggle", handler as any);
  }, []);

  // collapse flyouts while drawing to keep canvas clear
  useEffect(() => {
    if (app.state.cursorButton === "down") {
      dismissedRef.current = true;
      setOpenGroup(null);
    }
  }, [app.state.cursorButton]);

  // Auto-open corresponding flyout when selecting an element on canvas
  useEffect(() => {
    const selectedIds = appState.selectedElementIds || ({} as any);
    const key = Object.keys(selectedIds)
      .filter((id) => (selectedIds as any)[id])
      .sort()
      .join("|");
    if (key === prevSelectedKeyRef.current) {
      return;
    }
    prevSelectedKeyRef.current = key;

    if (!key) {
      // no selection → don't change currently open group
      return;
    }

    const selected = elements.find((el) => (selectedIds as any)[el.id]);
    if (!selected) {
      return;
    }
    let group: typeof openGroup = null;
    switch (selected.type as any) {
      case "text":
        group = "text";
        break;
      case "freedraw":
        group = "freedraw";
        break;
      case "line":
      case "arrow":
        group = "line";
        break;
      case "rectangle":
      case "diamond":
      case "ellipse":
      case "triangle":
      case "pentagon":
      case "callout":
      case "hollow_arrow":
      case "hollow_double_arrow":
        group = "shapes";
        break;
      default:
        group = null;
    }

    if (group) {
      dismissedRef.current = false;
      setOpenGroup(group);
    }
  }, [appState.selectedElementIds, elements]);

  // Reflect selected text properties (B/I/S/U, fontSize, align) into panel
  useEffect(() => {
    const ids = appState.selectedElementIds || ({} as any);
    const selected = elements.filter((el) => (ids as any)[el.id]);
    const selectedTexts = selected.filter((el) => (el as any).type === "text");
    if (selectedTexts.length === 0) {
      return;
    }
    const first = selectedTexts[0] as any;
    const allSame = (picker: (el: any) => any) =>
      selectedTexts.every((el) => picker(el) === picker(first));

    const styleOf = (el: any) => el.customData?.textStyle || {};

    const bold = allSame((el) => !!styleOf(el).bold)
      ? !!styleOf(first).bold
      : undefined;
    const italic = allSame((el) => !!styleOf(el).italic)
      ? !!styleOf(first).italic
      : undefined;
    const underline = allSame((el) => !!styleOf(el).underline)
      ? !!styleOf(first).underline
      : undefined;
    const strike = allSame((el) => !!styleOf(el).strikethrough)
      ? !!styleOf(first).strikethrough
      : undefined;
    const fontSize = allSame((el) => el.fontSize)
      ? (first.fontSize as number)
      : undefined;
    const textAlign = allSame((el) => el.textAlign)
      ? (first.textAlign as any)
      : undefined;

    const next: any = {};
    if (bold !== undefined) next.currentTextBold = bold;
    if (italic !== undefined) next.currentTextItalic = italic;
    if (underline !== undefined) next.currentTextUnderline = underline;
    if (strike !== undefined) next.currentTextStrikethrough = strike;
    if (fontSize !== undefined) next.currentItemFontSize = fontSize;
    if (textAlign !== undefined) next.currentItemTextAlign = textAlign;
    if (Object.keys(next).length) {
      (app as any).setState(next);
    }
  }, [appState.selectedElementIds, elements]);

  if (app.state.cursorButton === "down") {
    return null;
  }

  if (dismissedRef.current) {
    return null;
  }

  // if selection exists, allow opening its group's flyout even when active tool differs
  const selectedIds = appState.selectedElementIds || ({} as any);
  const selected = elements.find((el) => (selectedIds as any)[el.id]);
  const selectedType = selected?.type as any;
  const openForLineGroup =
    openGroup === "line" &&
    (activeTool === "line" ||
      activeTool === "arrow" ||
      selectedType === "line" ||
      selectedType === "arrow");
  const openForShapesGroup =
    (openGroup === "shapes" &&
      (
        [
          "rectangle",
          "diamond",
          "ellipse",
          "triangle",
          "pentagon",
          "callout",
          "hollow_arrow",
          "hollow_double_arrow",
        ] as any[]
      ).includes(activeTool as any)) ||
    (openGroup === "shapes" &&
      (
        [
          "rectangle",
          "diamond",
          "ellipse",
          "triangle",
          "pentagon",
          "callout",
          "hollow_arrow",
          "hollow_double_arrow",
        ] as any[]
      ).includes(selectedType));
  const openForText =
    openGroup === "text" && (activeTool === "text" || selectedType === "text");
  const openForFreedraw =
    openGroup === "freedraw" &&
    (activeTool === "freedraw" || selectedType === "freedraw");

  const isHighlighter = (appState.currentItemOpacity ?? 100) < 100;

  const syncWysiwygStyle = () => {
    const editable = document.querySelector(
      "textarea.excalidraw-wysiwyg",
    ) as HTMLTextAreaElement | null;
    if (!editable) return;
    const bold = !!(app.state as any).currentTextBold;
    const italic = !!(app.state as any).currentTextItalic;
    const underline = !!(app.state as any).currentTextUnderline;
    const strike = !!(app.state as any).currentTextStrikethrough;
    const decorations: string[] = [];
    if (underline) decorations.push("underline");
    if (strike) decorations.push("line-through");
    editable.style.fontWeight = bold ? "700" : "400";
    editable.style.fontStyle = italic ? "italic" : "normal";
    editable.style.textDecoration = decorations.join(" ") || "none";
  };

  const updateSelectedTextStyle = (partial: {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    strikethrough?: boolean;
  }) => {
    const selected = app.scene.getSelectedElements(app.state);
    const nextMap = app.scene.getElementsMapIncludingDeleted();
    const applyTo = (target: any) => {
      const prev = (target.customData?.textStyle || {}) as any;
      const updated = newElementWith(target, {
        customData: {
          ...(target.customData || {}),
          textStyle: {
            ...prev,
            ...(partial.bold !== undefined ? { bold: partial.bold } : {}),
            ...(partial.italic !== undefined ? { italic: partial.italic } : {}),
            ...(partial.underline !== undefined
              ? { underline: partial.underline }
              : {}),
            ...(partial.strikethrough !== undefined
              ? { strikethrough: partial.strikethrough }
              : {}),
          },
        },
      });
      nextMap.set(updated.id, updated);
      // recalc bounds in case metrics changed (bold/italic)
      const container = getContainerElement(
        updated,
        app.scene.getNonDeletedElementsMap(),
      );
      redrawTextBoundingBox(updated, container, app.scene as any);
    };

    for (const el of selected) {
      if (isTextElement(el)) {
        applyTo(el);
      } else {
        const bound = getBoundTextElement(
          el as any,
          app.scene.getNonDeletedElementsMap(),
        );
        if (bound) {
          applyTo(bound);
        }
      }
    }
    app.scene.replaceAllElements(nextMap);
    (app as any).store?.scheduleCapture?.();
    (app as any).refresh?.();
  };

  const updateCurrentTextDefaults = (partial: {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    strikethrough?: boolean;
  }) => {
    // 更新默认状态，影响“新创建”的文本
    const next: any = {};
    if (partial.bold !== undefined) next.currentTextBold = partial.bold;
    if (partial.italic !== undefined) next.currentTextItalic = partial.italic;
    if (partial.underline !== undefined)
      next.currentTextUnderline = partial.underline;
    if (partial.strikethrough !== undefined)
      next.currentTextStrikethrough = partial.strikethrough;
    (app as any).setState(next);
  };

  const setArrowType = (type: "line" | "arrow" | "double") => {
    if (type === "line") {
      app.setActiveTool({ type: "line", locked: true });
    } else if (type === "arrow") {
      app.setActiveTool({ type: "arrow", locked: true });
      (app as any).setState({
        currentItemStartArrowhead: null,
        currentItemEndArrowhead: "arrow",
      });
    } else {
      app.setActiveTool({ type: "arrow", locked: true });
      (app as any).setState({
        currentItemStartArrowhead: "arrow",
        currentItemEndArrowhead: "arrow",
      });
    }
  };

  const setArrowTypeOutline = (type: "arrow" | "double") => {
    if (type === "arrow") {
      app.setActiveTool({ type: "arrow", locked: true });
      (app as any).setState({
        currentItemStartArrowhead: null,
        currentItemEndArrowhead: "triangle_outline" as any,
      });
    } else {
      app.setActiveTool({ type: "arrow", locked: true });
      (app as any).setState({
        currentItemStartArrowhead: "triangle_outline" as any,
        currentItemEndArrowhead: "triangle_outline" as any,
      });
    }
  };

  const renderStrokeColor = () => {
    const colorValue = getFormValue<string | null>(
      elements,
      app as any,
      (element) => (element as any).strokeColor,
      true,
      (hasSelection) =>
        !hasSelection ? (appState as any).currentItemStrokeColor : null,
    ) as string | null;

    return (
      <ColorPicker
        type="elementStroke"
        label={t("labels.stroke")}
        color={colorValue ?? appState.currentItemStrokeColor}
        onChange={(color) =>
          actionManager.executeAction(actionChangeStrokeColor, "ui", {
            currentItemStrokeColor: color,
          })
        }
        elements={elements}
        appState={appState as any}
        palette={DEFAULT_ELEMENT_STROKE_COLOR_PALETTE}
        updateData={(formData) =>
          actionManager.executeAction(
            actionChangeStrokeColor,
            "ui",
            formData as any,
          )
        }
      />
    );
  };

  const setStrokeWidth = (value: number) => {
    actionManager.executeAction(actionChangeStrokeWidth, "ui", value as any);
  };

  const toggleHighlighter = (enable: boolean) => {
    (app as any).setState({ currentItemOpacity: enable ? 50 : 100 });
  };

  return (
    <div className="LeftToolFlyouts">
      {/* 线条/箭头 组 */}
      {openForLineGroup && (
        <Island className="left-flyout left-flyout--line">
          <Stack.Col gap={2} align="start">
            <div className="arrow-row">
              <ToolButton
                type="radio"
                aria-label={t("toolBar.line")}
                icon={LineIcon}
                checked={activeTool === "line"}
                name="left-flyout-line"
                title={t("toolBar.line")}
                onChange={() => setArrowType("line")}
              />
              <ToolButton
                type="radio"
                aria-label={t("toolBar.arrow")}
                icon={ArrowIcon}
                checked={
                  activeTool === "arrow" &&
                  appState.currentItemStartArrowhead === null &&
                  appState.currentItemEndArrowhead === "arrow"
                }
                name="left-flyout-line"
                title={t("toolBar.arrow")}
                onChange={() => setArrowType("arrow")}
              />
              <ToolButton
                type="radio"
                aria-label={t("labels.arrowtypes")}
                icon={DoubleArrowIcon}
                checked={
                  activeTool === "arrow" &&
                  appState.currentItemStartArrowhead === "arrow" &&
                  appState.currentItemEndArrowhead === "arrow"
                }
                name="left-flyout-line"
                title={t("labels.arrowtypes")}
                onChange={() => setArrowType("double")}
              />
            </div>
            <div className="left-flyout__separator" />
            {/* 描边宽度：线条/箭头组 */}
            <div className="left-flyout__title">{t("labels.strokeWidth")}</div>
            <div
              className="buttonList"
              style={{ display: "flex", gap: 6, justifyContent: "flex-start" }}
            >
              <ToolButton
                type="icon"
                aria-label={t("labels.thin")}
                icon={StrokeWidthBaseIcon}
                selected={selectedStrokeWidth === STROKE_WIDTH.thin}
                name="left-flyout-line-strokeWidth"
                title={t("labels.thin")}
                onClick={() =>
                  actionManager.executeAction(
                    actionChangeStrokeWidth,
                    "ui",
                    STROKE_WIDTH.thin as any,
                  )
                }
              />
              <ToolButton
                type="icon"
                aria-label={t("labels.bold")}
                icon={StrokeWidthBoldIcon}
                selected={selectedStrokeWidth === STROKE_WIDTH.bold}
                name="left-flyout-line-strokeWidth"
                title={t("labels.bold")}
                onClick={() =>
                  actionManager.executeAction(
                    actionChangeStrokeWidth,
                    "ui",
                    STROKE_WIDTH.bold as any,
                  )
                }
              />
              <ToolButton
                type="icon"
                aria-label={t("labels.extraBold")}
                icon={StrokeWidthExtraBoldIcon}
                selected={selectedStrokeWidth === STROKE_WIDTH.extraBold}
                name="left-flyout-line-strokeWidth"
                title={t("labels.extraBold")}
                onClick={() =>
                  actionManager.executeAction(
                    actionChangeStrokeWidth,
                    "ui",
                    STROKE_WIDTH.extraBold as any,
                  )
                }
              />
            </div>
            <div className="left-flyout__title">颜色</div>
            {renderStrokeColor()}
          </Stack.Col>
        </Island>
      )}

      {/* 形状网格 */}
      {openForShapesGroup && (
        <Island className="left-flyout left-flyout--shapes">
          {(() => {
            // derive current values from selection when present
            const strokeWidthValue = getFormValue(
              elements,
              app as any,
              (element) => (element as any).strokeWidth,
              (element) => (element as any).hasOwnProperty("strokeWidth"),
              (hasSelection) =>
                hasSelection ? null : (appState as any).currentItemStrokeWidth,
            ) as any;
            const strokeStyleValue = getFormValue(
              elements,
              app as any,
              (element) => (element as any).strokeStyle,
              (element) => (element as any).hasOwnProperty("strokeStyle"),
              (hasSelection) =>
                hasSelection ? null : (appState as any).currentItemStrokeStyle,
            ) as any;
            const roughnessValue = getFormValue(
              elements,
              app as any,
              (element) => (element as any).roughness,
              (element) => (element as any).hasOwnProperty("roughness"),
              (hasSelection) =>
                hasSelection ? null : (appState as any).currentItemRoughness,
            ) as any;
            // store on a temporary object for inline access in JSX below
            (globalThis as any).__leftShapesFormCache = {
              strokeWidthValue,
              strokeStyleValue,
              roughnessValue,
            };
            return null;
          })()}
          <div
            className="shapes-grid"
            style={{ gridTemplateColumns: "repeat(5, 1fr)" }}
          >
            <ToolButton
              type="radio"
              aria-label={t("toolBar.rectangle")}
              icon={RectangleIcon}
              checked={activeTool === "rectangle"}
              name="left-flyout-shapes"
              title={t("toolBar.rectangle")}
              onChange={() =>
                app.setActiveTool({ type: "rectangle", locked: true })
              }
            />
            <ToolButton
              type="radio"
              aria-label={t("toolBar.ellipse")}
              icon={EllipseIcon}
              checked={activeTool === "ellipse"}
              name="left-flyout-shapes"
              title={t("toolBar.ellipse")}
              onChange={() =>
                app.setActiveTool({ type: "ellipse", locked: true })
              }
            />
            <ToolButton
              type="radio"
              aria-label={t("toolBar.diamond")}
              icon={DiamondIcon}
              checked={activeTool === "diamond"}
              name="left-flyout-shapes"
              title={t("toolBar.diamond")}
              onChange={() =>
                app.setActiveTool({ type: "diamond", locked: true })
              }
            />
            <ToolButton
              type="radio"
              aria-label={t("toolBar.triangle")}
              icon={TriangleIcon}
              checked={activeTool === "triangle"}
              name="left-flyout-shapes"
              title={t("toolBar.triangle")}
              onChange={() =>
                app.setActiveTool({ type: "triangle", locked: true })
              }
            />
            <ToolButton
              type="radio"
              aria-label={t("toolBar.pentagon")}
              icon={PentagonIcon}
              checked={activeTool === "pentagon"}
              name="left-flyout-shapes"
              title={t("toolBar.pentagon")}
              onChange={() =>
                app.setActiveTool({ type: "pentagon", locked: true })
              }
            />
            <ToolButton
              type="radio"
              aria-label={t("toolBar.callout")}
              icon={CalloutIcon}
              checked={activeTool === ("callout" as any)}
              name="left-flyout-shapes"
              title={t("toolBar.callout")}
              onChange={() => app.setActiveTool({ type: "callout" as any })}
            />

            <ToolButton
              type="radio"
              aria-label={t("toolBar.hollowArrow")}
              icon={HollowArrowIcon}
              checked={activeTool === ("hollow_arrow" as any)}
              name="left-flyout-shapes"
              title={t("toolBar.hollowArrow")}
              onChange={() =>
                app.setActiveTool({ type: "hollow_arrow" as any })
              }
            />
            <ToolButton
              type="radio"
              aria-label={t("toolBar.hollowDoubleArrow")}
              icon={HollowDoubleArrowIcon}
              checked={activeTool === ("hollow_double_arrow" as any)}
              name="left-flyout-shapes"
              title={t("toolBar.hollowDoubleArrow")}
              onChange={() =>
                app.setActiveTool({ type: "hollow_double_arrow" as any })
              }
            />
          </div>

          {/* 颜色区域紧跟在图形网格后面 */}
          <div className="left-flyout__separator" />
          <div className="left-flyout__title" style={{ marginTop: 8 }}>
            {t("labels.stroke")}
          </div>
          {renderStrokeColor()}

          {/* 背景色：与截图里一致的色块+取色器，支持回显 */}
          <div className="left-flyout__title" style={{ marginTop: 8 }}>
            {t("labels.background")}
          </div>
          <ColorPicker
            type="elementBackground"
            label={t("labels.background")}
            color={
              getFormValue(
                elements,
                app as any,
                (element) => (element as any).backgroundColor,
                true,
                (hasSelection) =>
                  !hasSelection
                    ? (appState as any).currentItemBackgroundColor
                    : null,
              ) as any
            }
            onChange={(color) =>
              (actionManager as any).executeAction(
                actionChangeBackgroundColor as any,
                "ui",
                {
                  currentItemBackgroundColor: color,
                },
              )
            }
            elements={elements}
            appState={appState as any}
            updateData={(formData) =>
              (actionManager as any).executeAction(
                actionChangeBackgroundColor as any,
                "ui",
                formData as any,
              )
            }
          />

          {/* 移除下载按钮：应用户要求已删除 */}

          {/* 填充：单选 + 回显 */}
          <div className="left-flyout__title" style={{ marginTop: 6 }}>
            {t("labels.fill")}
          </div>
          {(() => {
            const fillStyleValue = getFormValue(
              elements,
              app as any,
              (element) => (element as any).fillStyle,
              (element) => (element as any).hasOwnProperty("fillStyle"),
              (hasSelection) =>
                hasSelection ? null : (appState as any).currentItemFillStyle,
            ) as any;
            (globalThis as any).__leftShapesFormCache = {
              ...((globalThis as any).__leftShapesFormCache || {}),
              fillStyleValue,
            };
            return null;
          })()}
          <div
            className="buttonList"
            style={{ display: "flex", gap: 6, justifyContent: "flex-start" }}
          >
            <ToolButton
              type="icon"
              aria-label={t("labels.hachure")}
              icon={FillHachureIcon}
              selected={
                (globalThis as any).__leftShapesFormCache?.fillStyleValue ===
                "hachure"
              }
              name="left-flyout-fillStyle"
              title={t("labels.hachure")}
              onClick={() =>
                actionManager.executeAction(
                  actionChangeFillStyle,
                  "ui",
                  "hachure" as any,
                )
              }
            />
            <ToolButton
              type="icon"
              aria-label={t("labels.crossHatch")}
              icon={FillCrossHatchIcon}
              selected={
                (globalThis as any).__leftShapesFormCache?.fillStyleValue ===
                "cross-hatch"
              }
              name="left-flyout-fillStyle"
              title={t("labels.crossHatch")}
              onClick={() =>
                actionManager.executeAction(
                  actionChangeFillStyle,
                  "ui",
                  "cross-hatch" as any,
                )
              }
            />
            <ToolButton
              type="icon"
              aria-label={t("labels.solid")}
              icon={FillSolidIcon}
              selected={
                (globalThis as any).__leftShapesFormCache?.fillStyleValue ===
                "solid"
              }
              name="left-flyout-fillStyle"
              title={t("labels.solid")}
              onClick={() =>
                actionManager.executeAction(
                  actionChangeFillStyle,
                  "ui",
                  "solid" as any,
                )
              }
            />
          </div>
          {/* 描边颜色与背景颜色放到“图形”下面：已在末尾保留颜色区域，这里先展示图形后再展示颜色块 */}
          {/* 描边宽度 */}
          <div className="left-flyout__title">{t("labels.strokeWidth")}</div>
          <div
            className="buttonList"
            style={{ display: "flex", gap: 6, justifyContent: "flex-start" }}
          >
            <ToolButton
              type="icon"
              aria-label={t("labels.thin")}
              icon={StrokeWidthBaseIcon}
              selected={selectedStrokeWidth === STROKE_WIDTH.thin}
              name="left-flyout-strokeWidth"
              title={t("labels.thin")}
              onClick={() =>
                actionManager.executeAction(
                  actionChangeStrokeWidth,
                  "ui",
                  STROKE_WIDTH.thin as any,
                )
              }
            />
            <ToolButton
              type="icon"
              aria-label={t("labels.bold")}
              icon={StrokeWidthBoldIcon}
              selected={selectedStrokeWidth === STROKE_WIDTH.bold}
              name="left-flyout-strokeWidth"
              title={t("labels.bold")}
              onClick={() =>
                actionManager.executeAction(
                  actionChangeStrokeWidth,
                  "ui",
                  STROKE_WIDTH.bold as any,
                )
              }
            />
            <ToolButton
              type="icon"
              aria-label={t("labels.extraBold")}
              icon={StrokeWidthExtraBoldIcon}
              selected={selectedStrokeWidth === STROKE_WIDTH.extraBold}
              name="left-flyout-strokeWidth"
              title={t("labels.extraBold")}
              onClick={() =>
                actionManager.executeAction(
                  actionChangeStrokeWidth,
                  "ui",
                  STROKE_WIDTH.extraBold as any,
                )
              }
            />
          </div>

          {/* 线条风格：固定朴素，隐藏控制项 */}
        </Island>
      )}

      {/* 文本工具 */}
      {openForText && (
        <Island className="left-flyout left-flyout--text">
          <Stack.Col gap={2} align="start">
            {/* 文本样式：B I S U 一行展示 */}
            <div className="left-flyout__title">样式</div>
            <div className="text-style-row" style={{ display: "flex", gap: 8 }}>
              <ToolButton
                type="icon"
                aria-label="Bold"
                title="Bold"
                icon={(<span style={{ fontWeight: 700 }}>B</span>) as any}
                selected={!!(appState as any).currentTextBold}
                onClick={() => {
                  const value = !(appState as any).currentTextBold;
                  (app as any).setState({ currentTextBold: value });
                  syncWysiwygStyle();
                  updateSelectedTextStyle({
                    bold: !(appState as any).currentTextBold,
                  });
                  updateCurrentTextDefaults({ bold: value });
                }}
              />
              <ToolButton
                type="icon"
                aria-label="Italic"
                title="Italic"
                icon={(<span style={{ fontStyle: "italic" }}>I</span>) as any}
                selected={!!(appState as any).currentTextItalic}
                onClick={() => {
                  const value = !(appState as any).currentTextItalic;
                  (app as any).setState({ currentTextItalic: value });
                  syncWysiwygStyle();
                  updateSelectedTextStyle({
                    italic: !(appState as any).currentTextItalic,
                  });
                  updateCurrentTextDefaults({ italic: value });
                }}
              />
              <ToolButton
                type="icon"
                aria-label="Strikethrough"
                title="Strikethrough"
                icon={
                  (
                    <span style={{ textDecoration: "line-through" }}>S</span>
                  ) as any
                }
                selected={!!(appState as any).currentTextStrikethrough}
                onClick={() => {
                  const value = !(appState as any).currentTextStrikethrough;
                  (app as any).setState({ currentTextStrikethrough: value });
                  syncWysiwygStyle();
                  updateSelectedTextStyle({
                    strikethrough: value,
                  });
                  updateCurrentTextDefaults({ strikethrough: value });
                }}
              />
              <ToolButton
                type="icon"
                aria-label="Underline"
                title="Underline"
                icon={
                  (
                    <span style={{ textDecoration: "underline" }}>U</span>
                  ) as any
                }
                selected={!!(appState as any).currentTextUnderline}
                onClick={() => {
                  const value = !(appState as any).currentTextUnderline;
                  (app as any).setState({ currentTextUnderline: value });
                  syncWysiwygStyle();
                  updateSelectedTextStyle({
                    underline: value,
                  });
                  updateCurrentTextDefaults({ underline: value });
                }}
              />
            </div>
            <div className="left-flyout__title">字号</div>
            {/* 字号：与其它选中样式保持一致（父元素蓝底，文字白色；hover 淡一些） */}
            <div
              className="text-size-row"
              style={{
                display: "flex",
                gap: 6,
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              {[10, 12, 16, 24, 36].map((size) => {
                const selected = appState.currentItemFontSize === size;
                return (
                  <button
                    key={size}
                    className={clsx("text-size-chip", { selected })}
                    style={{
                      minWidth: 32,
                      height: 28,
                      padding: "0 10px",
                      borderRadius: 8,
                      background: selected ? "#2178ff" : "transparent",
                      color: selected ? "#fff" : "var(--text-primary-color)",
                      border: selected
                        ? "1px solid #2178ff"
                        : "1px solid var(--button-border-color)",
                      transition: "background 0.12s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (selected)
                        e.currentTarget.style.background =
                          "rgba(33,120,255,0.85)";
                    }}
                    onMouseLeave={(e) => {
                      if (selected)
                        e.currentTarget.style.background = "#2178ff";
                    }}
                    onClick={() => {
                      actionManager.executeAction(
                        actionChangeFontSize,
                        "ui",
                        size as any,
                      );
                    }}
                  >
                    <span style={{ fontSize: 12 }}>{size}</span>
                  </button>
                );
              })}
            </div>
            <div className="left-flyout__title">对齐方式</div>
            {/* 对齐：一行简约风格，自定义三个按钮 */}
            <div
              className="text-align-row"
              style={{ display: "flex", gap: 6, justifyContent: "flex-start" }}
            >
              <ToolButton
                type="radio"
                aria-label={t("labels.left")}
                name="text-align"
                title={t("labels.left")}
                icon={TextAlignLeftIcon}
                checked={appState.currentItemTextAlign === "left"}
                onChange={() => {
                  actionManager.executeAction(
                    actionChangeTextAlign,
                    "ui",
                    "left",
                  );
                }}
              />
              <ToolButton
                type="radio"
                aria-label={t("labels.center")}
                name="text-align"
                title={t("labels.center")}
                icon={TextAlignCenterIcon}
                checked={appState.currentItemTextAlign === "center"}
                onChange={() => {
                  actionManager.executeAction(
                    actionChangeTextAlign,
                    "ui",
                    "center",
                  );
                }}
              />
              <ToolButton
                type="radio"
                aria-label={t("labels.right")}
                name="text-align"
                title={t("labels.right")}
                icon={TextAlignRightIcon}
                checked={appState.currentItemTextAlign === "right"}
                onChange={() => {
                  actionManager.executeAction(
                    actionChangeTextAlign,
                    "ui",
                    "right",
                  );
                }}
              />
            </div>

            <div className="left-flyout__separator" />
            {/* 颜色 */}
            {renderStrokeColor()}
          </Stack.Col>
        </Island>
      )}

      {/* 自由绘制：画笔 / 荧光笔 + 粗细 + 颜色 */}
      {openForFreedraw && (
        <Island className="left-flyout left-flyout--freedraw">
          <Stack.Col gap={2} align="start">
            {/* 笔型切换 */}
            <div className="freedraw-pen-toggle">
              <ToolButton
                type="radio"
                aria-label={t("toolBar.freedraw")}
                icon={FreedrawIcon}
                checked={!isHighlighter}
                name="freedraw-pen-type"
                title={t("toolBar.freedraw")}
                onChange={() => toggleHighlighter(false)}
              />
              <ToolButton
                type="radio"
                aria-label={t("toolBar.highlighter")}
                icon={HighlighterIcon}
                checked={isHighlighter}
                name="freedraw-pen-type"
                title={t("toolBar.highlighter")}
                onChange={() => toggleHighlighter(true)}
              />
            </div>

            {/* 粗细 */}
            <div className="left-flyout__title">{t("labels.strokeWidth")}</div>
            <div className="width-choices">
              {[
                STROKE_WIDTH.thin,
                STROKE_WIDTH.bold,
                STROKE_WIDTH.extraBold,
                6,
              ].map((w) => (
                <button
                  key={String(w)}
                  className={clsx("stroke-width-preview", {
                    selected: appState.currentItemStrokeWidth === w,
                  })}
                  title={`${t("labels.strokeWidth")} ${w}`}
                  onClick={() => setStrokeWidth(w as number)}
                >
                  <svg
                    width="24"
                    height="16"
                    viewBox="0 0 24 16"
                    aria-hidden="true"
                  >
                    <path
                      d="M2 12 C 6 2, 14 14, 22 6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={Math.max(1, Number(w))}
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              ))}
            </div>

            <div className="left-flyout__separator" />
            {/* 颜色 */}
            <div className="left-flyout__title">{t("colorPicker.color")}</div>
            <div className="color-section">{renderStrokeColor()}</div>
          </Stack.Col>
        </Island>
      )}
    </div>
  );
};

export default LeftToolFlyouts;
